import {
  ARMY,
  BOSSES,
  BUDGET,
  bossAt,
  bossBaseHp,
  mareaBudgetBp,
  mareaInkBp,
  DAMAGE_TYPES,
  ELITE_FROM_MAREA,
  HOSTS,
  HOST_IDS,
  MAREAS,
  NIGHTMARES,
  OMEN_TYPE_SHARE_BP,
  PLAN,
  PLAN_IDS,
  PLANS,
  UNSLEEPING,
  WAVE,
  type BossType,
  type DamageType,
  type EnemyKind,
  type HostId,
  type NightmareType,
  type PlanId,
  type SquadRole,
  type UnsleepingId,
} from '@somnhold/content';
import { idiv, imax, imin, mulBp, pow075Bp, rdiv, sqrtBp } from './math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngRange, rngWeighted, seedRng, type RngState } from './math/rng.ts';
import type { Spawn } from './types.ts';

export interface WaveSpec {
  /** Campaign seed: the composition of a wave is fixed by it (so "adelanto" matches the real wave). */
  seed: number;
  marea: number;
  wave: number;
  sectors: number;
  /** Total activity in bp (sum of player weights, min 1.0). */
  activityBp: number;
  depth: 'light' | 'deep';
  /** +20 % after a lost Marea. */
  lossPenalty: boolean;
  /** Override the type table (practice). */
  weights?: Partial<Record<NightmareType, number>>;
  /** Override total budget per sector (practice/incursion). */
  budgetOverride?: number;
  /** Override the spawn window in ticks (practice). */
  spawnWindow?: number;
  /** Chance per nightmare of grade 2 regardless of depth (practice escalation), applied to the last third. */
  lateInkBp?: number;
  /** Added to the Marea's table (a harder campaign), unlike `weights` it keeps warlords and bosses. */
  extraWeights?: Partial<Record<NightmareType, number>>;
  /** Scales the computed budget (a harder campaign). */
  budgetBp?: number;
  /** First Marea with a warlord per wave (default ELITE_FROM_MAREA). */
  eliteFromMarea?: number;
  /** Chance per group to come from a point on the edge of the land instead of its path. */
  wildBp?: number;
  /** Chance per path group to leave its path near the wall and storm it beside its gate. */
  flankBp?: number;
  /** Draw a war host for the wave (bastion waves; docs/CRIATURAS.md §1). Off, the table is the Marea's as is. */
  hosts?: boolean;
  /** Draw a battle plan for the wave (bastion waves; docs/TACTICAS.md). */
  plans?: boolean;
  /** This plan (or none) instead of the draw (tests, probes). */
  plan?: PlanId | null;
  /** The army a general drafted for the wave instead of the draw (fitted to the budget and ARMY's limits). */
  army?: ArmyPick;
  /** One of the Unsleeping comes in the warlord's place (docs/ANTIHEROES.md): his health, his blows' scale, his returns, the hero he hunts. */
  unsleeping?: { kind: UnsleepingId; hp: number; dmgBp: number; returns: number; hunt?: string };
  /**
   * The step the group's level calls for (campaign/level.ts, WAVE.levelMarea), in place of the calendar's Marea for
   * everything but the draws' seeds: table, hosts, plans, ink, warlord, flanks and the budget's base (without the
   * calendar's ramp inside a Marea). Bastion waves always carry it (docs/DECISIONS.md, 2026-11-03).
   */
  tableMarea?: number;
  /** Health and damage of every nightmare of the wave (not the boss) for heroes past the Marea's level (WAVE.levelStrengthBp). */
  levelStrengthBp?: number;
  /** The boss's health, scaled (the campaign's pressure, PRESSURE). */
  bossBp?: number;
}

/** The step whose creatures and budget a wave brings: the one the heroes' level calls for, or the calendar's Marea without one. */
const creaturesOf = (spec: WaveSpec): number => spec.tableMarea ?? spec.marea;

/** A sector's points before the scales: the level's step without the calendar's ramp, or the calendar's Marea and wave. */
const baseBudget = (spec: WaveSpec): number =>
  waveBudget(creaturesOf(spec), spec.tableMarea !== undefined ? 0 : spec.wave, spec.activityBp, spec.sectors, spec.lossPenalty);

/** A drafted army: how many of each kind, per sector. */
export type ArmyPick = { sector: number; kind: NightmareType; n: number }[];

/** The kinds a general may draft for a wave: the table's (before any host), never a boss or one only escorted. */
export const draftableKinds = (spec: WaveSpec): NightmareType[] => {
  const table = baseTable(spec);
  return (Object.keys(table) as NightmareType[]).filter((k) => (table[k] ?? 0) > 0 && !NIGHTMARES[k].escorted).sort();
};

/** The points a general may spend on a wave, all sectors together (no host: the army is its own). */
export const draftBudget = (spec: WaveSpec): number => {
  const plan = planFor(spec);
  const per = spec.budgetOverride ?? mulBp(mulBp(baseBudget(spec), spec.budgetBp ?? 10000), plan ? PLANS[plan].budgetBp : 10000);
  return mulBp(per * spec.sectors, ARMY.budgetBp);
};

/**
 * A drafted army made to fit: only draftable kinds and real sectors; no kind past `ARMY.maxKindBp` of the
 * budget, no sector past `ARMY.maxSectorBp` (with two or more), all of it within the budget (trimmed from the
 * largest buys). Null when what is left has fewer than `ARMY.minKinds` kinds: the wave is drawn as ever.
 */
export const fitArmy = (spec: WaveSpec, pick: ArmyPick): ArmyPick | null => {
  const kinds = new Set(draftableKinds(spec));
  const budget = draftBudget(spec);
  const by = new Map<string, { sector: number; kind: NightmareType; n: number }>();
  for (const x of pick) {
    if (!kinds.has(x.kind) || !Number.isInteger(x.sector) || x.sector < 0 || x.sector >= spec.sectors || !Number.isInteger(x.n) || x.n <= 0) continue;
    const key = `${x.sector}:${x.kind}`;
    const cur = by.get(key);
    if (cur) cur.n += x.n;
    else by.set(key, { sector: x.sector, kind: x.kind, n: x.n });
  }
  // Ascending sector, then kind: the trims below go the same way every time.
  const out = [...by.values()].sort((a, b) => a.sector - b.sector || (a.kind < b.kind ? -1 : a.kind > b.kind ? 1 : 0));
  for (const x of out) x.n = imin(x.n, NIGHTMARES[x.kind].perSector ?? x.n);
  const cost = (xs: typeof out): number => xs.reduce((a, x) => a + x.n * NIGHTMARES[x.kind].points, 0);
  // Each kind to its cap, then each sector to its cap (cut evenly across what it holds).
  const capTo = (xs: typeof out, cap: number): void => {
    const c = cost(xs);
    if (c <= cap) return;
    for (const x of xs) x.n = idiv(x.n * cap, c);
  };
  for (const k of kinds) capTo(out.filter((x) => x.kind === k), mulBp(budget, ARMY.maxKindBp));
  if (spec.sectors > 1) for (let i = 0; i < spec.sectors; i++) capTo(out.filter((x) => x.sector === i), mulBp(budget, ARMY.maxSectorBp));
  capTo(out, budget);
  const kept = out.filter((x) => x.n > 0);
  return new Set(kept.map((x) => x.kind)).size >= ARMY.minKinds ? kept : null;
};

export interface WaveComposition {
  spawns: Spawn[];
  budget: number;
  boss: BossType | null;
  bossHp: number;
  bossSector: number;
  /** The battle plan, and its squads (a role in a sector; spawns point at them by index). */
  plan: PlanId | null;
  squads: { role: SquadRole; sector: number }[];
}

/** Points per sector: round(base × mareaBp[m] × (1 + 0.12w) × activity^0.75 / sectors). */
export const waveBudget = (marea: number, wave: number, activityBp: number, sectors: number, lossPenalty: boolean): number => {
  const m = mareaBudgetBp(marea);
  const w = 10000 + wave * BUDGET.waveStepBp;
  const a = pow075Bp(activityBp);
  // 100 × m/1e4 × w/1e4 × a/1e4 / sectors, carried in bp to round once at the end.
  let v = idiv(BUDGET.base * m * w, 10000);
  v = idiv(v * a, 10000);
  if (lossPenalty) v = mulBp(v, 10000 + BUDGET.lossPenaltyBp);
  return rdiv(v, 10000 * sectors);
};

/** Boss HP scale: activity^0.75. */
export const bossHp = (type: BossType, activityBp: number, marea = BOSSES[type].marea): number => mulBp(bossBaseHp(type, marea), pow075Bp(activityBp));

/** The wave's table before any host: the Marea's (or the override), plus what a harder campaign or the era adds. */
const baseTable = (spec: WaveSpec): Partial<Record<NightmareType, number>> => {
  const def = MAREAS[imin(creaturesOf(spec), MAREAS.length - 1)] as (typeof MAREAS)[number];
  const weights: Partial<Record<NightmareType, number>> = spec.weights ? { ...spec.weights } : { ...def.weights };
  if (!spec.weights && spec.extraWeights) for (const k of Object.keys(spec.extraWeights).sort() as NightmareType[]) weights[k] = (weights[k] ?? 0) + (spec.extraWeights[k] ?? 0);
  return weights;
};

/**
 * The war host of a wave: drawn from the seed alone (its own stream, so the wave's draws don't move), among
 * the hosts this Marea allows whose kinds are in the table, never the one the wave before drew. Without
 * `hosts` (or with a fixed table) it is always 'common', and so is a boss's wave: the boss is its host.
 */
export const hostFor = (spec: WaveSpec): HostId => {
  if (!spec.hosts || spec.weights || (spec.army && fitArmy({ ...spec, army: undefined }, spec.army))) return 'common';
  if (bossAt(spec.marea, spec.wave) !== null) return 'common';
  const table = baseTable(spec);
  const prev = spec.wave > 0 ? hostFor({ ...spec, wave: spec.wave - 1 }) : null;
  const options = HOST_IDS.filter((id) => {
    const h = HOSTS[id];
    if (id === prev || creaturesOf(spec) < h.from) return false;
    return id === 'common' || (Object.keys(h.mult) as NightmareType[]).some((k) => (table[k] ?? 0) > 0);
  }).map((id) => [id, HOSTS[id].weight] as const);
  if (options.length === 0) return 'common';
  return rngWeighted(seedRng(deriveSeed(spec.seed, 50000 + spec.marea * 16 + spec.wave)), options);
};

/** The table under a host: weights ×10 for grain, each kind scaled by the host's share for it. */
const hostTable = (table: Partial<Record<NightmareType, number>>, id: HostId): Partial<Record<NightmareType, number>> => {
  if (id === 'common') return table;
  const h = HOSTS[id];
  const out: Partial<Record<NightmareType, number>> = {};
  for (const k of Object.keys(table).sort() as NightmareType[]) out[k] = idiv((table[k] ?? 0) * (h.mult[k] ?? h.rest), 1000);
  return out;
};

/**
 * The battle plan of a wave (docs/TACTICAS.md): drawn from the seed alone (its own stream), among the plans this
 * Marea allows whose needs are in the table, against a wave with none. Never with a fixed table or a boss.
 */
export const planFor = (spec: WaveSpec): PlanId | null => {
  if (spec.plan !== undefined) return spec.plan;
  if (!spec.plans || spec.weights) return null;
  if (bossAt(spec.marea, spec.wave) !== null) return null;
  const table = baseTable(spec);
  const options: (readonly [PlanId | 'none', number])[] = [['none', PLAN.noneWeight]];
  for (const id of PLAN_IDS) {
    const p = PLANS[id];
    if (creaturesOf(spec) < p.from) continue;
    if (p.needs && !p.needs.some((k) => (table[k] ?? 0) > 0)) continue;
    options.push([id, p.weight]);
  }
  const id = rngWeighted(seedRng(deriveSeed(spec.seed, 60000 + spec.marea * 16 + spec.wave)), options);
  return id === 'none' ? null : id;
};

/**
 * The role of each of a sector's groups under a plan (an escorted one keeps the role of the group it walks in);
 * none for a mammoth outside an escorted siege: it never waits with the bulk, it walks straight in.
 */
const rolesFor = (plan: PlanId, groups: { kind: NightmareType; n: number; escorted?: boolean }[]): (SquadRole | undefined)[] => {
  const roles: (SquadRole | undefined)[] = [];
  const ground = groups.reduce((a, g) => a + (NIGHTMARES[g.kind].flying || NIGHTMARES[g.kind].siege ? 0 : g.n * NIGHTMARES[g.kind].points), 0);
  let taken = 0;
  for (const g of groups) {
    const d = NIGHTMARES[g.kind];
    if (g.escorted && roles.length > 0) {
      roles.push(roles[roles.length - 1]);
      continue;
    }
    if (d.flying) {
      roles.push('wings');
      continue;
    }
    if (d.tramples && plan !== 'escortedSiege') {
      roles.push(undefined);
      continue;
    }
    if (plan === 'escortedSiege') {
      if (d.siege) roles.push('engines');
      else if (PLAN.guardKinds.includes(g.kind) && taken * 10000 < ground * PLAN.guardShareBp) {
        taken += g.n * d.points;
        roles.push('guard');
      } else roles.push('main');
      continue;
    }
    // A rush: all of it is the bulk. A pincer: the ground groups take turns between its two halves.
    if (plan === 'rush') {
      roles.push('main');
      continue;
    }
    if (plan === 'pincer') {
      roles.push(taken++ % 2 === 0 ? 'main' : 'second');
      continue;
    }
    // A feint: the first ground groups, up to its share, are the decoy; the rest is the bulk.
    if (taken * 10000 < ground * PLAN.decoyShareBp) {
      taken += g.n * d.points;
      roles.push('decoy');
    } else roles.push('main');
  }
  return roles;
};

/** A drafted army's groups in a sector: each kind split into its usual group sizes, the kinds taking turns. */
const armyGroups = (rng: RngState, army: ArmyPick, sector: number): { kind: NightmareType; n: number }[] => {
  const queues = army
    .filter((x) => x.sector === sector)
    .map((x) => {
      const out: number[] = [];
      let left = x.n;
      while (left > 0) {
        const size = NIGHTMARES[x.kind].group;
        const n = imin(left, size ? rngRange(rng, size[0], size[1]) : rngRange(rng, WAVE.groupMin, WAVE.groupMax));
        out.push(n);
        left -= n;
      }
      return { kind: x.kind, sizes: out };
    });
  const groups: { kind: NightmareType; n: number }[] = [];
  for (let round = 0; queues.some((q) => round < q.sizes.length); round++) for (const q of queues) if (round < q.sizes.length) groups.push({ kind: q.kind, n: q.sizes[round] as number });
  return groups;
};

/** When the i-th of a sector's g groups comes in a staged wave (after the lead-in): its stage, and its place in it. */
const stageAt = (i: number, g: number): number => {
  let from = 0;
  for (const p of WAVE.stages) {
    const to = from + p.shareBp;
    if (i * 10000 < g * to || p === WAVE.stages[WAVE.stages.length - 1]) {
      const a = idiv(g * from, 10000);
      const n = imax(1, idiv(g * to, 10000) - a);
      return p.at + idiv(imax(0, i - a) * p.len, n);
    }
    from = to;
  }
  return 0;
};

export const generateWave = (spec: WaveSpec): WaveComposition => {
  const rng = seedRng(deriveSeed(spec.seed, spec.marea * 16 + spec.wave + 1));
  const host = hostFor(spec);
  const plan = planFor(spec);
  const squads: { role: SquadRole; sector: number }[] = [];
  const weights = hostTable(baseTable(spec), host);
  const entries = (Object.keys(weights) as NightmareType[])
    .sort()
    .map((k) => [k, weights[k] as number] as const)
    .filter(([, w]) => w > 0);
  const staged = !plan && spec.spawnWindow === undefined && spec.hosts === true && WAVE.stages.length > 0;
  const budget =
    spec.budgetOverride ??
    mulBp(mulBp(mulBp(baseBudget(spec), spec.budgetBp ?? 10000), HOSTS[host].budgetBp), plan ? PLANS[plan].budgetBp : 10000);
  const inkBp = spec.depth === 'deep' ? mareaInkBp(creaturesOf(spec)) : 0;
  const spawns: Spawn[] = [];
  const army = spec.army ? fitArmy({ ...spec, army: undefined }, spec.army) : null;
  for (let sector = 0; sector < spec.sectors; sector++) {
    let left = army ? 0 : budget;
    const groups: { kind: NightmareType; n: number; escorted?: boolean }[] = army ? armyGroups(rng, army, sector) : [];
    let guard = 0;
    // How many of a kind with a cap per sector (the mammoth) were drawn here so far.
    const drawn = new Map<NightmareType, number>();
    const room = (k: NightmareType): number => (NIGHTMARES[k].perSector ?? 0x7fffffff) - (drawn.get(k) ?? 0);
    while (left > 0 && guard++ < WAVE.maxGroups) {
      const affordable = entries.filter(([k]) => NIGHTMARES[k].points <= left && room(k) > 0);
      if (affordable.length === 0) break;
      const kind = rngWeighted(rng, affordable);
      const cost = NIGHTMARES[kind].points;
      const size = NIGHTMARES[kind].group;
      let n = size ? rngRange(rng, size[0], size[1]) : rngRange(rng, WAVE.groupMin, WAVE.groupMax);
      const max = imin(idiv(left, cost), room(kind));
      if (n > max) n = max;
      if (NIGHTMARES[kind].perSector) drawn.set(kind, (drawn.get(kind) ?? 0) + n);
      groups.push(NIGHTMARES[kind].escorted && groups.length > 0 ? { kind, n, escorted: true } : { kind, n });
      left -= n * cost;
    }
    // Past the cap on groups, what is left inks the ones drawn (as a chance, so it spreads).
    const spent = budget - left;
    const overflowBp = guard > WAVE.maxGroups && left > 0 && spent > 0 ? imin(10000, rdiv(left * 10000, mulBp(spent, WAVE.overflowInkCostBp))) : 0;
    // Past the ink too, what is still left makes them stronger (health and damage, each by the root).
    const beyond = overflowBp >= 10000 ? left - mulBp(spent, WAVE.overflowInkCostBp) : 0;
    const overflowStrengthBp = beyond > 0 ? sqrtBp(10000 + rdiv(beyond * 10000, mulBp(spent, WAVE.overflowStrengthCostBp))) : 10000;
    const strengthBp = spec.levelStrengthBp && spec.levelStrengthBp > 10000 ? mulBp(overflowStrengthBp, spec.levelStrengthBp) : overflowStrengthBp;
    // Stagger groups over the spawn window; members trickle in 8 ticks apart.
    const g = groups.length;
    const window = spec.spawnWindow ?? WAVE.spawnWindow;
    let prev = { base: 0, edge: -1, flank: -1 };
    // Under a plan: a squad per role in this sector; the ones that open the fight (engines, their guard, a
    // decoy) come in the first part of the window, in order; the rest keep their time and wait when there.
    const roles = plan ? rolesFor(plan, groups) : [];
    const squadOf = new Map<SquadRole, number>();
    for (const r of roles) if (r && !squadOf.has(r)) squadOf.set(r, squads.push({ role: r, sector }) - 1);
    const opening = roles.filter((r) => r === 'engines' || r === 'guard' || r === 'decoy').length;
    let opened = 0;
    groups.forEach((grp, i) => {
      // An escorted one (the mourner) comes in among the group before it, the same way it came.
      const escort = grp.escorted === true;
      let base = escort ? prev.base + 4 : WAVE.leadIn + (g <= 1 ? 0 : staged ? stageAt(i, g) : idiv(i * window, g)) + rngInt(rng, 24);
      const late = spec.lateInkBp !== undefined && i * 3 >= g * 2;
      // Rolled only when asked, so waves without it keep their exact composition.
      const edge = escort ? prev.edge : spec.wildBp && rngChance(rng, spec.wildBp) ? rngInt(rng, 10000) : -1;
      // A path group may storm the wall beside its gate instead (rolled only when asked, like the wild).
      let flank = escort ? prev.flank : edge < 0 && spec.flankBp && rngChance(rng, spec.flankBp) ? rngInt(rng, 10000) : -1;
      // Under a plan, the groups from the wild stay skirmishers on their own; the ones by the paths are its
      // squads, and go where it sends them (the bulk of a siege keeps its flank: the assault storms the wall
      // there, not only the breach).
      const role = edge >= 0 ? undefined : roles[i];
      const opens = role === 'engines' || role === 'guard' || role === 'decoy';
      if (opens && !escort) base = WAVE.leadIn + idiv(opened * idiv(window * PLAN.openingBp, 10000), imax(1, opening)) + (base % 24);
      if (opens) opened++;
      if (role && !(plan === 'escortedSiege' && role === 'main')) flank = -1;
      if (!escort) prev = { base, edge, flank };
      for (let j = 0; j < grp.n; j++) {
        const ink = late ? (spec.lateInkBp as number) : imax(inkBp, overflowBp);
        const tier: 1 | 2 = ink > 0 && rngChance(rng, ink) ? 2 : 1;
        spawns.push({
          tick: base + j * 8,
          sector,
          kind: grp.kind,
          tier,
          elite: false,
          ...(edge >= 0 ? { edge } : {}),
          ...(flank >= 0 ? { flank } : {}),
          ...(role ? { squad: squadOf.get(role) as number } : {}),
          ...(strengthBp > 10000 ? { strengthBp } : {}),
        });
      }
    });
  }
  // One warlord per wave from Marea 2: one grade up, crowned (in a staged wave, with the last stage).
  if (creaturesOf(spec) >= (spec.eliteFromMarea ?? ELITE_FROM_MAREA) && spawns.length > 0 && !spec.weights) {
    const last = WAVE.stages[WAVE.stages.length - 1];
    const closing = staged && last ? spawns.flatMap((x, k) => (x.tick >= WAVE.leadIn + last.at ? [k] : [])) : [];
    const i = closing.length ? (closing[rngInt(rng, closing.length)] as number) : rngInt(rng, spawns.length);
    const sp = spawns[i] as Spawn;
    const un = spec.unsleeping;
    // One of the Unsleeping takes the warlord's place: he walks in mid-wave where the warlord would have, his guard with him.
    if (un) spawns.push({ tick: WAVE.leadIn + mulBp(WAVE.spawnWindow, UNSLEEPING.arriveBp), sector: sp.sector, kind: un.kind, tier: sp.tier, elite: false, hp: un.hp, un: { dmgBp: un.dmgBp, returns: un.returns, ...(un.hunt ? { hunt: un.hunt } : {}) } });
    else {
      sp.elite = true;
      sp.tier = sp.tier === 1 ? 2 : 3;
    }
  }
  let boss: BossType | null = null;
  let hp = 0;
  let bossSector = 0;
  const bt = spec.weights ? null : bossAt(spec.marea, spec.wave);
  if (bt) {
    boss = bt;
    hp = mulBp(bossHp(bt, spec.activityBp, creaturesOf(spec)), spec.bossBp ?? 10000);
    bossSector = rngInt(rng, spec.sectors);
    spawns.push({ tick: WAVE.leadIn + idiv(WAVE.spawnWindow, 3), sector: bossSector, kind: bt, tier: spec.depth === 'deep' ? 2 : 1, elite: false });
  }
  spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  return { spawns, budget, boss, bossHp: hp, bossSector, plan, squads };
};

/**
 * The damage type a wave mostly brings (the omen names it): the non-physical type with the most points in
 * it, if those are at least OMEN_TYPE_SHARE_BP of the wave's points; null otherwise.
 */
export const dominantType = (spawns: readonly { kind: EnemyKind }[]): DamageType | null => {
  const by = new Map<DamageType, number>();
  let total = 0;
  for (const sp of spawns) {
    const d = NIGHTMARES[sp.kind];
    total += d.points;
    if (d.dmgType && d.dmgType !== 'physical') by.set(d.dmgType, (by.get(d.dmgType) ?? 0) + d.points);
  }
  let best: DamageType | null = null;
  let bp = 0;
  for (const t of DAMAGE_TYPES) {
    const p = by.get(t) ?? 0;
    if (p > bp) {
      bp = p;
      best = t;
    }
  }
  return best && bp * 10000 >= total * OMEN_TYPE_SHARE_BP ? best : null;
};

